import React, { useState } from 'react'
import { Card, CardBody, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { AlertBanner } from '@/design-system/components/AlertBanner'
import { usePendingImages } from './hooks/useAdmin'
import type { PendingImage, ImageFilters } from './hooks/useAdmin'

function RejectModal({ image, onConfirm, onClose }: {
  image: PendingImage
  onConfirm: (reason: string) => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-raised border border-surface-border rounded-xl w-full max-w-md shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
        <div className="px-5 py-4 border-b border-surface-border">
          <h3 className="text-sm font-semibold text-text-primary">Avvisa bild</h3>
          <p className="text-xs text-text-muted mt-0.5">{image.mission_title} — {image.photographer_name}</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-medium text-text-secondary">Anledning (visas för Zoomern)</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Beskriv varför bilden avvisas…"
            className="w-full px-3 py-2 rounded-lg bg-surface-overlay border border-surface-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-accent resize-none"
          />
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Avbryt</Button>
          <Button variant="danger" size="sm" disabled={!reason.trim()} onClick={() => onConfirm(reason)}>
            Avvisa bild
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ImageReview() {
  const [statusFilter, setStatusFilter] = useState<ImageFilters['status']>('pending')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [rejectTarget, setRejectTarget] = useState<PendingImage | null>(null)
  const [batchSuccess, setBatchSuccess] = useState(false)

  const { data, isLoading, approveImage, rejectImage } = usePendingImages({ status: statusFilter })

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBatchApprove = async () => {
    for (const id of selected) {
      await approveImage(id)
    }
    setSelected(new Set())
    setBatchSuccess(true)
    setTimeout(() => setBatchSuccess(false), 3000)
  }

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectTarget) return
    await rejectImage(rejectTarget.id, reason)
    setRejectTarget(null)
  }

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

  const formatGps = (lat: number | null, lng: number | null) =>
    lat && lng ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : '—'

  const pending = data.filter(i => i.approved === null)

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Bildgranskning</h1>
          <p className="text-sm text-text-secondary mt-1">
            {pending.length} bilder väntar på granskning
          </p>
        </div>
        {selected.size > 0 && (
          <Button variant="primary" size="sm" onClick={handleBatchApprove}>
            ✓ Godkänn {selected.size} markerade
          </Button>
        )}
      </div>

      {batchSuccess && (
        <AlertBanner
          variant="success"
          title={`${selected.size} bilder godkändes`}
          description="Fotograferna notifieras automatiskt."
        />
      )}

      <Card>
        <CardHeader className="flex flex-wrap gap-2">
          {(['pending', 'approved', 'rejected', 'all'] as const).map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setSelected(new Set()) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'bg-brand-accent text-white'
                  : 'bg-surface-overlay text-text-secondary hover:text-text-primary border border-surface-border'
              }`}
            >
              {s === 'pending' ? 'Väntande' : s === 'approved' ? 'Godkända' : s === 'rejected' ? 'Avvisade' : 'Alla'}
            </button>
          ))}
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-surface-border animate-pulse">
                  <div className="h-48 bg-surface-overlay" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-surface-overlay rounded w-3/4" />
                    <div className="h-3 bg-surface-overlay rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="py-12 text-center text-text-muted text-sm">
              Inga bilder i denna kategori
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.map(image => {
                const isSelected = selected.has(image.id)
                const statusBadge = image.approved === true ? 'complete' : image.approved === false ? 'urgent' : 'pending'
                const statusLabel = image.approved === true ? 'Godkänd' : image.approved === false ? 'Avvisad' : 'Väntar'

                return (
                  <div
                    key={image.id}
                    className={`rounded-xl overflow-hidden border transition-all ${
                      isSelected
                        ? 'border-brand-accent ring-2 ring-brand-accent/30'
                        : 'border-surface-border hover:border-surface-overlay'
                    } bg-surface-raised`}
                  >
                    {/* Thumbnail */}
                    <div className="relative">
                      <img
                        src={image.photo_url}
                        alt="Submitted photo"
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      {/* Select checkbox — only for pending */}
                      {image.approved === null && (
                        <button
                          onClick={() => toggleSelect(image.id)}
                          className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-brand-accent border-brand-accent'
                              : 'bg-black/40 border-white/60 hover:border-white'
                          }`}
                        >
                          {isSelected && <span className="text-white text-xs">✓</span>}
                        </button>
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge variant={statusBadge}>{statusLabel}</Badge>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-3 space-y-1.5">
                      <p className="text-xs font-semibold text-text-primary truncate">{image.mission_title}</p>
                      <p className="text-xs text-text-secondary">{image.photographer_name}</p>
                      <p className="text-xs text-text-muted">{formatTime(image.created_at)}</p>
                      <p className="text-xs text-text-muted font-mono">📍 {formatGps(image.latitude, image.longitude)}</p>

                      {image.rejection_reason && (
                        <p className="text-xs text-brand-danger bg-brand-danger/10 rounded-lg px-2 py-1.5 mt-1">
                          {image.rejection_reason}
                        </p>
                      )}

                      {/* Actions */}
                      {image.approved === null && (
                        <div className="flex gap-2 pt-1">
                          <Button
                            variant="primary"
                            size="sm"
                            fullWidth
                            onClick={() => approveImage(image.id)}
                          >
                            ✓ Godkänn
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            fullWidth
                            onClick={() => setRejectTarget(image)}
                          >
                            Avvisa
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          image={rejectTarget}
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}
    </div>
  )
}
